
import type React from "react"
import { createContext, useContext, useEffect, useState } from "react"
import { useTheme as useNextTheme } from "next-themes"

interface ThemeContextType {
  isDarkMode: boolean
  colorTheme: "default" | "red"
  toggleDarkMode: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useNextTheme()
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [colorTheme, setColorTheme] = useState<"default" | "red">("red")
  const [mounted, setMounted] = useState(false)

  // Ensure component is mounted before rendering to avoid hydration mismatch
  useEffect(() => {
    setMounted(true)

    // Load saved dark mode preference from localStorage
    const savedTheme = localStorage.getItem("theme")
    if (savedTheme) {
      setTheme(savedTheme)
      setIsDarkMode(savedTheme === "dark")
    } else {
      // Default to light unless the user explicitly opts into dark
      setTheme("light")
      setIsDarkMode(false)
    }
  }, [setTheme])

  // Update isDarkMode when theme changes
  useEffect(() => {
    if (mounted) {
      setIsDarkMode(theme === "dark")
      // Save dark mode preference to localStorage
      localStorage.setItem("theme", theme || "light")
    }
  }, [theme, mounted])

  const toggleDarkMode = () => {
    const newTheme = theme === "dark" ? "light" : "dark"
    setTheme(newTheme)
    setIsDarkMode(newTheme === "dark")
  }

  const value = {
    isDarkMode,
    colorTheme,
    toggleDarkMode,
  }

  // Avoid rendering anything until mounted to prevent hydration mismatch
  if (!mounted) return <>{children}</>

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export const useCustomTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error("useCustomTheme must be used within a ThemeProvider")
  }
  return context
}
